import type { HsMatrix4 } from '../objects/matrix4.class';
import { HsVector3 } from '../objects/vector3.class';
import type { HsBox } from './box.class';
import { HsPlane } from './plane.class';
import type { HsSphere } from './sphere.class';

const PLANE_COUNT: number = 6;

export class HsFrustum {
  public readonly planes: [HsPlane, HsPlane, HsPlane, HsPlane, HsPlane, HsPlane];

  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();

  constructor() {
    const plane0: HsPlane = HsPlane.makePlaneXY();
    const plane1: HsPlane = HsPlane.makePlaneXY();
    const plane2: HsPlane = HsPlane.makePlaneXY();
    const plane3: HsPlane = HsPlane.makePlaneXY();
    const plane4: HsPlane = HsPlane.makePlaneXY();
    const plane5: HsPlane = HsPlane.makePlaneXY();
    this.planes = [plane0, plane1, plane2, plane3, plane4, plane5];
  }

  /**
   * Sets set.
   * @param planes planes.
   * @returns Result value.
   */
  public set(planes: [HsPlane, HsPlane, HsPlane, HsPlane, HsPlane, HsPlane]): HsFrustum {
    this.planes[0].copy(planes[0]);
    this.planes[1].copy(planes[1]);
    this.planes[2].copy(planes[2]);
    this.planes[3].copy(planes[3]);
    this.planes[4].copy(planes[4]);
    this.planes[5].copy(planes[5]);
    return this;
  }

  /**
   * Sets from projection matrix.
   * @param projectionMatrix projection matrix.
   * @returns Result value.
   */
  public setFromProjectionMatrix(projectionMatrix: HsMatrix4): HsFrustum {
    const v: number[] = projectionMatrix.values;
    this.planes[0].setComponents(v[3] - v[0], v[7] - v[4], v[11] - v[8], v[15] - v[12]).normalize();
    this.planes[1].setComponents(v[3] + v[0], v[7] + v[4], v[11] + v[8], v[15] + v[12]).normalize();
    this.planes[2].setComponents(v[3] + v[1], v[7] + v[5], v[11] + v[9], v[15] + v[13]).normalize();
    this.planes[3].setComponents(v[3] - v[1], v[7] - v[5], v[11] - v[9], v[15] - v[13]).normalize();
    this.planes[4].setComponents(v[3] - v[2], v[7] - v[6], v[11] - v[10], v[15] - v[14]).normalize();
    this.planes[5].setComponents(v[3] + v[2], v[7] + v[6], v[11] + v[10], v[15] + v[14]).normalize();
    return this;
  }

  /**
   * Copies copy.
   * @param frustum frustum.
   * @returns Result value.
   */
  public copy(frustum: HsFrustum): HsFrustum {
    this.set(frustum.planes);
    return this;
  }

  /**
   * Handles has intersection with sphere.
   * @param sphere sphere.
   * @returns Result value.
   */
  public hasIntersectionWithSphere(sphere: HsSphere): boolean {
    const centerX: number = sphere.center.x;
    const centerY: number = sphere.center.y;
    const centerZ: number = sphere.center.z;
    const radius: number = sphere.radius;
    for (let i: number = 0; i < PLANE_COUNT; i++) {
      const plane: HsPlane = this.planes[i];
      const normal: HsVector3 = plane.normal;
      const distance: number = normal.x * centerX + normal.y * centerY + normal.z * centerZ + plane.offset;
      if (distance < -radius) {
        return false;
      }
    }
    return true;
  }

  /**
   * Handles has intersection with box.
   * @param box box.
   * @returns Result value.
   */
  public hasIntersectionWithBox(box: HsBox): boolean {
    const vector: HsVector3 = HsFrustum.#vector3Temp2;
    for (let i: number = 0; i < PLANE_COUNT; i++) {
      const vectorX: number = this.planes[i].normal.x > 0 ? box.max.x : box.min.x;
      const vectorY: number = this.planes[i].normal.y > 0 ? box.max.y : box.min.y;
      const vectorZ: number = this.planes[i].normal.z > 0 ? box.max.z : box.min.z;
      vector.set(vectorX, vectorY, vectorZ);
      if (HsPlane.distance(this.planes[i], vector) < 0) {
        return false;
      }
    }
    return true;
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsFrustum): HsFrustum {
    const frustum: HsFrustum = new HsFrustum();
    frustum.set([
      HsPlane.makeCopy(source.planes[0]),
      HsPlane.makeCopy(source.planes[1]),
      HsPlane.makeCopy(source.planes[2]),
      HsPlane.makeCopy(source.planes[3]),
      HsPlane.makeCopy(source.planes[4]),
      HsPlane.makeCopy(source.planes[5]),
    ]);
    return frustum;
  }
}
